import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getSchedulerApiUrl,
  getSchedulerAuthToken,
  schedulerApiFetch,
} from '../api/schedulerClient';
import { useInvalidateSessionList } from '../queries/useSessionList';
import SchedulerNotificationStack from '../components/SchedulerNotificationStack';
import { getSchedulerStatusNotification } from '../utils/schedulerStatusNotifications';

const SchedulerStatusContext = createContext(null);

export function useSchedulerStatus() {
  const ctx = useContext(SchedulerStatusContext);
  if (!ctx) {
    throw new Error('useSchedulerStatus must be used within SchedulerStatusProvider');
  }
  return ctx;
}

let notificationIdCounter = 0;

/** Scheduler /status — polled globally while a run is active. */
export function SchedulerStatusProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const invalidateSessionList = useInvalidateSessionList();
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [statusPollError, setStatusPollError] = useState('');
  const [lastSchedulerApiError, setLastSchedulerApiError] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const prevRunningRef = useRef(undefined);
  const prevStatusSnapshotRef = useRef(null);
  const statusInitializedRef = useRef(false);

  const dismissNotification = useCallback((id) => {
    setNotifications((current) => current.filter((item) => item.id !== id));
  }, []);

  const pushNotification = useCallback((payload) => {
    notificationIdCounter += 1;
    setNotifications((current) => [
      ...current.slice(-2),
      { id: notificationIdCounter, ...payload },
    ]);
  }, []);

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const authToken = await getSchedulerAuthToken();
      if (!authToken) {
        return null;
      }

      const response = await schedulerApiFetch('status', {
        bearerToken: authToken,
      });

      if (response.ok) {
        const status = await response.json();
        setSchedulerStatus(status);
        setStatusPollError('');
        if (status.last_api_error) {
          setLastSchedulerApiError(status.last_api_error);
        } else {
          setLastSchedulerApiError(null);
        }
        return status;
      }

      setStatusPollError(`API returned status ${response.status}`);
      return null;
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
      const schedulerApiUrl = getSchedulerApiUrl();
      setStatusPollError(
        `Cannot connect to scheduler API at ${schedulerApiUrl}. Make sure the FastAPI server is running on this machine.`
      );
      return null;
    }
  }, []);

  useEffect(() => {
    if (!schedulerStatus) {
      return;
    }

    if (!statusInitializedRef.current) {
      statusInitializedRef.current = true;
      prevStatusSnapshotRef.current = schedulerStatus;
      return;
    }

    const previous = prevStatusSnapshotRef.current;
    prevStatusSnapshotRef.current = schedulerStatus;

    const transition = getSchedulerStatusNotification(previous, schedulerStatus);
    if (transition) {
      pushNotification(transition);
    }
  }, [schedulerStatus, pushNotification]);

  useEffect(() => {
    if (authLoading || !user) {
      setSchedulerStatus(null);
      setStatusPollError('');
      setLastSchedulerApiError(null);
      setPollingActive(false);
      setNotifications([]);
      prevRunningRef.current = undefined;
      prevStatusSnapshotRef.current = null;
      statusInitializedRef.current = false;
      return undefined;
    }

    fetchSchedulerStatus();
  }, [authLoading, user, fetchSchedulerStatus]);

  useEffect(() => {
    if (!schedulerStatus) {
      prevRunningRef.current = undefined;
      return;
    }

    const wasRunning = prevRunningRef.current;
    const nowRunning = Boolean(schedulerStatus.is_running);
    prevRunningRef.current = nowRunning;

    if (nowRunning) {
      setPollingActive(true);
    }

    if (wasRunning === true && nowRunning === false) {
      setPollingActive(false);
      invalidateSessionList();
    }
  }, [schedulerStatus, invalidateSessionList]);

  useEffect(() => {
    if (authLoading || !user || !pollingActive) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchSchedulerStatus();
    }, 2000);

    return () => clearInterval(intervalId);
  }, [authLoading, user, pollingActive, fetchSchedulerStatus]);

  const markSchedulerPollingActive = useCallback(() => {
    setPollingActive(true);
  }, []);

  const value = useMemo(
    () => ({
      schedulerStatus,
      fetchSchedulerStatus,
      statusPollError,
      lastSchedulerApiError,
      markSchedulerPollingActive,
    }),
    [schedulerStatus, fetchSchedulerStatus, statusPollError, lastSchedulerApiError, markSchedulerPollingActive]
  );

  return (
    <SchedulerStatusContext.Provider value={value}>
      {children}
      <SchedulerNotificationStack
        notifications={notifications}
        onDismiss={dismissNotification}
      />
    </SchedulerStatusContext.Provider>
  );
}
